import { VuexModule, Module, Mutation, Action, getModule } from 'vuex-module-decorators'
import { BoundingBox, Point } from './asset'
import { store } from '@/store'
import { AppModule, App } from '@/store/modules/app'
import { Railway, Asset } from '@/rest/models'
import { TextWithSize } from '@/models/text'
import { AddRailwayRequest } from '@/rest/apis'

export interface RailwayState {
    title: string
    userInputTitle: string
    giftOption: boolean
}

export type ProductName = {
    [key in string]: string
}

export class RailwayTransfer implements Railway {

    public sessionId?: string
    public title: string
    public stickerText?: TextWithSize
    public asset?: Asset
    public gift?: boolean

    constructor(sessionId: string | undefined, title: string) {
        this.title = title
        this.sessionId = sessionId
    }

}

@Module({ dynamic: true, store, name: 'railway' })
class RailwayImpl extends VuexModule implements RailwayState {
    public title = ""
    public userInputTitle = ""
    public dedication = new TextWithSize("", 0)
    public userInputDedication = ""
    public giftOption = false
    public railwayAssetId = -1
    public railwayImageBoundingBox = { ll: [0,0] as Point, ur: [0,0] as Point }
    public railwayImageRotation = 0
    public deletingAsset = false

    @Mutation
    public SET_TITLE(title: string) {
        this.title = title
    }

    @Mutation
    public SET_USER_INPUT_TITLE(userInputTitle: string) {
        this.userInputTitle = userInputTitle
    }

    @Mutation
    public SET_DEDICATION(dedication: TextWithSize) {
        this.dedication = dedication
    }

    @Mutation
    public SET_USER_INPUT_DEDICATION(userInputDedication: string) {
        this.userInputDedication = userInputDedication
    }

    @Mutation
    public SET_GIFT_OPTION(giftOption: boolean) {
        this.giftOption = giftOption
    }

    @Mutation
    private SET_RAILWAY_IMAGE_ASSET_ID(imageAssetId: number) {
        this.railwayAssetId = imageAssetId
    }

    @Mutation
    private SET_RAILWAY_IMAGE_BOUNDING_BOX(boundingBox: BoundingBox) {
        this.railwayImageBoundingBox = boundingBox
    }

    @Mutation
    private SET_RAILWAY_IMAGE_ROTATION(rotation: number) {
        this.railwayImageRotation = rotation
    }

    @Mutation
    public SET_DELETING_ASSET(deletingAsset: boolean) {
        this.deletingAsset = deletingAsset
    }

    @Action({ commit: 'SET_TITLE' })
    public SetTitle(title: string) {
        return title
    }

    @Action({ commit: 'SET_USER_INPUT_TITLE' })
    public SetUserInputTitle(title: string) {
        return title
    }

    @Action({ commit: 'SET_DEDICATION' })
    public SetDedication(dedication: TextWithSize) {
        return dedication
    }

    @Action({ commit: 'SET_USER_INPUT_DEDICATION' })
    public SetUserInputDedication(userInputDedication: string) {
        return userInputDedication
    }

    @Action({ commit: 'SET_GIFT_OPTION' })
    public SetGiftOption(giftOption: boolean) {
        return giftOption
    }

    @Action({ commit: 'SET_RAILWAY_IMAGE_ASSET_ID' })
    public SetRailwayImageAssetId(railwayAssetId: number) {
        return railwayAssetId
    }

    @Action({ commit: 'SET_RAILWAY_IMAGE_BOUNDING_BOX' })
    public SetRailwayImageBoundingBox(boundingBox: BoundingBox) {
        return boundingBox
    }

    @Action({ commit: 'SET_RAILWAY_IMAGE_ROTATION' })
    public SetRailwayImageRotation(rotation: number) {
        return rotation
    }

    @Action({ commit: 'SET_DELETING_ASSET' })
    public SetDeletingAsset(deletingAsset: boolean) {
        return deletingAsset
    }

    get showDesignTrain(): boolean {
        let step = AppModule.steps[AppModule.currentStepIndex]
        return step.componentName === "enter-title"
    }

    get showTitle(): boolean {
        let step = AppModule.steps[AppModule.currentStepIndex]
        return step.componentName === "enter-title" && !AppModule.showMenu
    }

    get showDedication(): boolean {
        let step = AppModule.steps[AppModule.currentStepIndex]
        return step.componentName === "enter-dedication"
    }

    get showImageUpload(): boolean {
        let step = AppModule.steps[AppModule.currentStepIndex]
        return step.componentName === "railway-image-upload"
    }

    get showBox3D(): boolean {
        return (AppModule.steps[1].completed || AppModule.steps[2].completed) && AppModule.showMenu
    }

    get designStickerChanged(): boolean {
        return AppModule.steps[1].completed || AppModule.steps[2].completed
    }

    get railwayTransfer() :AddRailwayRequest {
        let railway = new RailwayTransfer(AppModule.session.sessionId, RailwayModule.title)

        if (RailwayModule.railwayAssetId !== -1) {
            let railwayAsset = {
                id: RailwayModule.railwayAssetId,
                rotation: RailwayModule.railwayImageRotation,
                ll: RailwayModule.railwayImageBoundingBox.ll,
                ur: RailwayModule.railwayImageBoundingBox.ur,
                pieces: "railway_1"
            }
            railway.asset = railwayAsset
        }

        railway.stickerText = new TextWithSize(RailwayModule.dedication.text, Math.round(RailwayModule.dedication.size * 2.34))

        railway.gift = RailwayModule.giftOption

        let addRailwayRequest: AddRailwayRequest = {railway: railway}
        return addRailwayRequest;
    }

    get showToolbar(): boolean {
        let step = AppModule.steps[AppModule.currentStepIndex]
        return step.componentName === "railway-image-upload"
    }
}

export const RailwayModule = getModule(RailwayImpl)
